import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { AlertsService } from '../../services/alerts/alerts.service';
import { NafathService } from '../../services/nafath/nafath.service';
import { AlertActionEnum } from '../../enums/alert-action.enum';
import { ROUTE_PATHS } from '../../utils/route-paths.const';

/**
 * Nafath Guard
 * Protects Nafath routes using alerts API and stored verification data:
 * 1. Only accessible when alerts says action.key === 'submit_nafath'
 * 2. INITIATE: Only for users without national ID (first time)
 * 3. UPDATE: Only for users with national ID (retry/update/renewal)
 * 4. VERIFY: Requires OTP stored in cookies
 *
 * Usage in routes:
 * - Apply to nafath child routes that need protection
 */
export const nafathGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): boolean => {
  const authService = inject(AuthService);
  const alertsService = inject(AlertsService);
  const nafathService = inject(NafathService);
  const router = inject(Router);

  const redirectToHome = (): boolean => {
    router.navigate([ROUTE_PATHS.HOME], {
      replaceUrl: true,
      state: { bypassGuard: true },
    });
    return false;
  };

  const redirectToUpdate = (): boolean => {
    router.navigate([ROUTE_PATHS.NAFATH, ROUTE_PATHS.UPDATE], {
      replaceUrl: true,
      state: { bypassGuard: true },
    });
    return false;
  };

  const redirectToInitiate = (): boolean => {
    router.navigate([ROUTE_PATHS.NAFATH, ROUTE_PATHS.INITIATE], {
      replaceUrl: true,
      state: { bypassGuard: true },
    });
    return false;
  };

  // Check alerts: only allow nafath when the API says submit_nafath is required
  if (!alertsService.isActionRequired(AlertActionEnum.SUBMIT_NAFATH)) {
    return redirectToHome();
  }

  // Sub-route specific logic
  const routePath = route.routeConfig?.path;
  const authData = authService.authData();
  const nationalId = authData?.nationalId;

  switch (routePath) {
    case ROUTE_PATHS.INITIATE:
      // If user has national ID, they should use UPDATE route
      if (nationalId) {
        return redirectToUpdate();
      }
      return true;

    case ROUTE_PATHS.VERIFY: {
      // Requires OTP stored from initiate/update step
      const storedOtp = nafathService.getNafathOtp();
      if (!storedOtp) {
        return redirectToHome();
      }
      return true;
    }

    case ROUTE_PATHS.UPDATE:
      // If no national ID, redirect to initiate
      if (!nationalId) {
        return redirectToInitiate();
      }
      return true;

    default:
      return true;
  }
};
